import { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { FiltersDropdown } from '@shared/components/ui/FiltersDropdown';
import { Search } from '@shared/components/ui/Search';
import { useQueryParams } from '@shared/hooks/useQueryParams';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StockMovementsFiltersPanel } from './StockMovementsFiltersPanel';

interface FiltersSectionProps {
  handleSearch: (value: string) => void;
  onFilterChange?: (conditions: FilterConditionOutput[]) => void;
}

export const FiltersSection = ({ handleSearch, onFilterChange }: FiltersSectionProps) => {
  const { t } = useTranslation('stockmovementsModule');

  // 🎯 Hook useQueryParams para sincronizar con URL
  const { params, updateParam } = useQueryParams({
    search: { type: 'string', default: '' },
    filters: { type: 'json', default: [] as FilterConditionOutput[] },
  });

  // 🎯 Estado local sincronizado con URL
  const [inputValue, setInputValue] = useState(params.search ?? '');
  const filtersFromUrl = useMemo(() => (Array.isArray(params.filters) ? params.filters : []), [params.filters]);

  const activeFiltersCount = useMemo(() => filtersFromUrl.length, [filtersFromUrl]);

  // 🎯 Efecto para sincronizar con cambios en URL
  useEffect(() => {
    const searchFromUrl = params.search ?? '';
    setInputValue((prev) => (prev !== searchFromUrl ? searchFromUrl : prev));

    handleSearch(searchFromUrl);
    onFilterChange?.(filtersFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.search, filtersFromUrl]);

  const handleFilterChange = useCallback(
    (conditions: FilterConditionOutput[]) => {
      updateParam('filters', conditions);
      onFilterChange?.(conditions);
    },
    [updateParam, onFilterChange],
  );

  // 🎯 Handler para el cambio del input - Solo afecta al estado local
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  // 🎯 Handler para ejecutar búsqueda - Solo comunica al padre cuando necesario
  const handleSearchSubmit = useCallback(() => {
    const trimmedValue = inputValue.trim();
    updateParam('search', trimmedValue);
    handleSearch(trimmedValue);
  }, [inputValue, updateParam, handleSearch]);

  return (
    <>
      <Search
        placeholder={t('search.placeholder')}
        value={inputValue}
        onChange={handleInputChange}
        onEnter={handleSearchSubmit}
        dataTestId="search"
        inputDataTestId="search-input"
      />
      <FiltersDropdown
        title={t('filters.title', 'Filtros')}
        activeFiltersCount={activeFiltersCount}
        triggerTestId="filters-toggle-button"
        dropdownContentClassName="sm:min-w-[720px] sm:max-w-[720px] p-3 bg-body-bg text-foreground border border-card-border max-h-[min(90vh,720px)] overflow-hidden flex flex-col"
      >
        <StockMovementsFiltersPanel initialConditions={filtersFromUrl} onChange={handleFilterChange} />
      </FiltersDropdown>
    </>
  );
};
